import React from "react";
import PropTypes from "prop-types";
import { Download } from "lucide-react";

import Accordion from "./Accordion";
import Button from "./Button";
import { formatFileSize, formatUploadDate } from "./FileField";
import PackFormDetails from "./PackFormDetails";
import { DefinitionGrid, DetailGroup, formatDistanceUnit, formatPackDate, formatSiteDate } from "./PackDetailPrimitives";
import PackRt3199Details from "./PackRt3199Details";
import StatusBadge from "./StatusBadge";
import { packSectionIcon } from "./packSections";

const formatDate = formatPackDate;
const distance = (value, unit) => value?.[unit] || value?.milesYards || value?.milesChains || value?.kilometersMeters;
const activeForms = (forms = {}) => Object.entries(forms).filter(([, form]) => (
  form && (Number(form.active) === 1 || form.active === true || Number(form.is_disabled) === 0)
));
const formNames = {
  live_line_tester: ["Live line tester", "NR/L3/ELP/3091"],
  eng_train: ["ES VMF for engineering trains & OTM", "NR/L3/OPS/084F04"],
  otp_trolley: ["ES VMF for OTP & Trolley", "NR/L3/OPS/084F05"],
  equipment_placement_removal: ["PSE process record of equipment placement / removal", "NR/L3/OPS/255FC"],
  record_of_movement: ["ES Record of movements", "NR/L3/OPS/255FA"],
};
const attachmentGroups = {
  won: { label: "WON", type: "draft" },
  whiteboard: { label: "Whiteboard", type: "secondary" },
  other: { label: "Other", type: "offer" },
};

const attachmentGroup = (upload = {}) => (
  attachmentGroups[String(upload.collection || upload.collection_name || "other").toLowerCase()]
  || attachmentGroups.other
);

const PackDetailSections = ({ onDownload, pack }) => {
  const possessions = Array.isArray(pack.possessions) ? pack.possessions : [];
  const users = [
    ...(pack.users?.esusers || []).map((user) => ({ ...user, role: "Engineering Supervisor" })),
    ...(pack.users?.cossusers || []).map((user) => ({ ...user, role: "COSS" })),
    ...(pack.users?.cossXusers || []).map((user) => ({ ...user, role: "External COSS" })),
  ];
  const lines = possessions.flatMap((possession, possessionIndex) => (possession.worksites || []).flatMap((worksite) => (
    (worksite.lines_at_site || []).map((line) => ({
      ...line,
      possessionIndex,
      possession_ref: possession.possession_ref || `Possession ${possessionIndex + 1}`,
      rt3199: line.rt3199 || {},
      worksite_ref: worksite.worksite_ref,
    }))
  )));
  const forms = activeForms(pack.forms);
  const uploads = [...(pack.uploads || []), ...(pack.media || [])];
  const distanceUnitLabel = formatDistanceUnit(pack.conversion_unit);

  return (
    <section aria-labelledby="pack-details-title" className="wsm-pack-details">
      <div className="wsm-pack-details__heading">
        <h2 id="pack-details-title">Pack details</h2>
        <p>Review the operational information and supporting records included in this pack.</p>
      </div>

      <Accordion className="wsm-pack-details__accordion">
        <Accordion.Item anchorId="possessions" description={possessions.length + " part" + (possessions.length === 1 ? "" : "s")} icon={packSectionIcon("possessions")} title="Possessions, worksites and lines">
          <div className="wsm-pack-detail__stack">
            {possessions.length === 0 && <p className="wsm-pack-detail__empty">No possessions recorded.</p>}
            {possessions.map((possession, index) => (
              <section className="wsm-pack-detail" key={possession.id || index}>
                <div className="wsm-pack-detail__header">
                  <div><h3>Part {index + 1}</h3><p>{possession.worksites?.length ?? 0} {possession.worksites?.length === 1 ? "worksite" : "worksites"}</p></div>
                </div>
                <DefinitionGrid items={[
                  { label: "Planned start", value: formatDate(possession.planned_start_date) },
                  { label: "Planned end", value: formatDate(possession.planned_end_date) },
                  { label: "Level crossings", value: String((possession.level_crossings || []).flat().length) },
                  { label: "Marker boards", value: String((possession.marker_boards || []).flat().length) },
                ]} />
                {(possession.possession_limits || []).map((limit, limitIndex) => <DetailGroup key={limit.id || limitIndex} title={`Possession limit ${limitIndex + 1}`}><DefinitionGrid items={[
                  { label: "Start mileage", value: limit.start_miles }, { label: "End mileage", value: limit.end_miles },
                ]} /></DetailGroup>)}
                {(possession.worksites || []).map((worksite, worksiteIndex) => <DetailGroup key={worksite.id || worksiteIndex} description={worksite.isSuspend ? "Suspended worksite" : undefined} title={worksite.worksite_ref || `Worksite ${worksiteIndex + 1}`}>
                  <div className="wsm-pack-detail__stack">{(worksite.lines_at_site || []).map((line, lineIndex) => <section className="wsm-pack-detail__subrecord" key={line.line_reference || lineIndex}>
                    <div className="wsm-pack-detail__header"><h3>{line.name || `Line ${lineIndex + 1}`}</h3></div>
                    <DefinitionGrid items={[
                      { label: "From ELR", value: line.from_elr?.elr }, { label: "To ELR", value: line.to_elr?.elr },
                      { label: `Start (${distanceUnitLabel})`, value: distance(line.start, pack.conversion_unit) }, { label: `End (${distanceUnitLabel})`, value: distance(line.end, pack.conversion_unit) },
                      { label: "Start marker board required", value: Number(line.marker_board_start) === 1 ? "Yes" : "No" }, { label: "End marker board required", value: Number(line.marker_board_end) === 1 ? "Yes" : "No" },
                      { label: `Possession start (${distanceUnitLabel})`, value: distance(line.poss_start, pack.conversion_unit) }, { label: `Possession end (${distanceUnitLabel})`, value: distance(line.poss_end, pack.conversion_unit) },
                    ]} />
                  </section>)}</div>
                </DetailGroup>)}
                {(possession.level_crossings || []).flat().map((crossing, crossingIndex) => <DetailGroup key={crossing.id || crossingIndex} title={`Level crossing ${crossingIndex + 1}`}><DefinitionGrid items={[
                  { label: "Level crossing", value: crossing.level_crossing }, { label: "Operational status", value: crossing.operational_status },
                  { label: "Lines at site", value: (crossing.lines || []).map((line) => line.name || line.line_affected).filter(Boolean).join(", ") },
                  { label: "Arranged", value: formatSiteDate(crossing.date_arranged) }, { label: "Withdrawn", value: formatSiteDate(crossing.date_withdrawn) },
                ]} /></DetailGroup>)}
                {(possession.marker_boards || []).flat().map((board, boardIndex) => <DetailGroup key={board.id || boardIndex} title={`Marker board ${boardIndex + 1}`}><DefinitionGrid items={[
                  { label: "Name", value: board.name }, { label: "Location", value: board.location },
                ]} /></DetailGroup>)}
              </section>
            ))}
          </div>
        </Accordion.Item>

        <Accordion.Item anchorId="users" description={users.length + " assigned " + (users.length === 1 ? "person" : "people")} icon={packSectionIcon("users")} title="People">
          {users.length === 0 ? <p className="wsm-pack-detail__empty">No users assigned.</p> : (
            <ul className="wsm-pack-detail__people">
              {users.map((user, index) => (
                <li key={user.user_id || index}>
                  <div><strong>{user.name || "Unnamed user"}</strong><span>{user.role}</span></div>
                  <span>{[user.location, user.tasks, user.phone_no, user.start_date && user.end_date ? `${formatDate(user.start_date)} to ${formatDate(user.end_date)}` : null].filter(Boolean).join(" \u00b7 ") || "No additional details"}</span>
                </li>
              ))}
            </ul>
          )}
        </Accordion.Item>

        <Accordion.Item anchorId="lines" description={lines.length + " line" + (lines.length === 1 ? "" : "s")} icon={packSectionIcon("lines")} title="Lines and RT3199">
          <div className="wsm-pack-detail__stack">
            {lines.length === 0 && <p className="wsm-pack-detail__empty">No lines recorded.</p>}
            {lines.map((line, index) => (
              <section className="wsm-pack-detail" key={line.line_reference || index}>
                <div className="wsm-pack-detail__header">
                  <div><h3>{line.name || "Line " + (index + 1)}</h3><p>{line.worksite_ref}</p></div>
                </div>
                <PackRt3199Details conversionUnit={pack.conversion_unit || "milesYards"} handovers={pack.handovers || []} line={line} location={pack.location} possessions={possessions} users={pack.users || {}} />
              </section>
            ))}
          </div>
        </Accordion.Item>

        <Accordion.Item anchorId="forms" description={forms.length + " active form" + (forms.length === 1 ? "" : "s")} icon={packSectionIcon("forms")} title="Forms">
          {forms.length === 0 ? <p className="wsm-pack-detail__empty">No forms are active for this pack.</p> : <div className="wsm-pack-detail__stack">
            {forms.map(([key, form]) => <section className="wsm-pack-detail wsm-pack-detail--form" key={key}>
              <div className="wsm-pack-detail__header wsm-pack-detail__form-header"><div><h3>{formNames[key]?.[0] || key.replaceAll("_", " ")}</h3><p>{formNames[key]?.[1]}</p></div><StatusBadge type="success">Included</StatusBadge></div>
              <div className="wsm-pack-detail__form-body">
                <PackFormDetails conversionUnit={pack.conversion_unit || "milesYards"} form={form} formKey={key} location={pack.location} />
              </div>
            </section>)}
          </div>}
        </Accordion.Item>

        <Accordion.Item anchorId="uploads" description={uploads.length + " supporting " + (uploads.length === 1 ? "file" : "files")} icon={packSectionIcon("uploads")} title="Attachments">
          {uploads.length === 0 ? <p className="wsm-pack-detail__empty">No supporting files uploaded.</p> : (
            <ul className="wsm-pack-detail__records">
              {uploads.map((upload, index) => {
                const uploadDate = formatUploadDate(upload.uploaded_at || upload.created_at || upload.updated_at);
                const uploadSize = upload.size && Number.isFinite(Number(upload.size)) ? formatFileSize(Number(upload.size)) : upload.size;
                return <li key={upload.id || upload.path || index}>
                  <div>
                    <strong>{upload.name || upload.filename || "Supporting file"}</strong>
                    <span className="wsm-pack-detail__attachment-meta">
                      <StatusBadge type={attachmentGroup(upload).type}>{attachmentGroup(upload).label}</StatusBadge>
                      {upload.type || upload.category || "Upload"}{uploadSize ? ", " + uploadSize : ""}{uploadDate ? ` \u00b7 Uploaded ${uploadDate}` : ""}
                    </span>
                  </div>
                  {onDownload && <Button aria-label={"Download " + (upload.name || upload.filename || "file")} icon={Download} onClick={() => onDownload(upload)} size="compact" variant="ghost">Download</Button>}
                </li>;
              })}
            </ul>
          )}
        </Accordion.Item>
      </Accordion>
    </section>
  );
};

PackDetailSections.propTypes = {
  onDownload: PropTypes.func,
  pack: PropTypes.object.isRequired,
};

export default PackDetailSections;
